/**
 * Plan board → external tracker sync.
 *
 * Built on httpClient (so it gets API_BASE and `credentials: 'include'`) rather than the bare
 * `fetch` convention of api/github.ts — this talks to our own Express API, not to GitHub. All the
 * GitHub traffic happens server-side; nothing credential-shaped crosses the wire from here.
 */
import { postJson, putJson, ErrorResponse } from './httpClient';
import { ExternalItemLink, IntegrationProvider, ProjectIntegrations } from '../types';

/** Per-item outcome counts for one run. */
export interface SyncItemsReport {
    /** Issues newly created on the tracker. */
    created: number;
    /** Existing issues whose title/body had drifted and were rewritten. */
    updated: number;
    /** Links confirmed still valid upstream. */
    verified: number;
    /** Links whose issue had been deleted on the tracker, so the card was re-created. */
    relinked: number;
    /** Placements whose underlying story node no longer exists. */
    stale: number;
    /** Newly-created issues added to the configured project board. */
    boarded: number;
    /** Links whose board column could not be read this run. */
    statusUnknown: number;
    failed: Array<{ itemId: string; title: string; error: string }>;
}

/** Per-connection outcome counts for one run. */
export interface SyncRelationsReport {
    applied: number;
    removed: number;
    unchanged: number;
    /** Connectors with no relationship type, or an endpoint that has no link. */
    skipped: number;
    failed: Array<{ connectionId: string | null; error: string }>;
}

export interface SyncReport {
    ok: true;
    provider: IntegrationProvider;
    repoFullName: string;
    syncedAt: string;
    items: SyncItemsReport;
    relations: SyncRelationsReport;
    links: Record<string, ExternalItemLink>;
    /** Human-readable notes, e.g. a native API being unavailable on this repo. */
    warnings: string[];
}

/**
 * An error the UI wants to act on rather than just display — `github_not_connected` and
 * `no_repo_selected` point the user at Settings, `sync_in_progress` means "wait".
 */
export interface SyncErrorResponse extends ErrorResponse {
    code?: string;
}

/**
 * Runs a sync for the whole project. Creates issues for cards placed on the Timeline canvas that
 * are not already linked, verifies the links that exist, and reconciles connectors into
 * issue-to-issue links.
 *
 * `repoFullName` is what the browser has in its device-local preference; the server prefers its
 * own stored value once it has one, so this only matters for the very first sync.
 */
export function syncProject(
    projectId: string,
    provider: IntegrationProvider,
    target: { repoFullName?: string; projectV2Id?: string } = {},
): Promise<SyncReport | SyncErrorResponse> {
    return postJson<SyncReport>(`/api/projects/${projectId}/sync/${provider}`, target, {
        networkErrorMessage: 'Could not reach the server. Check your connection and try again.',
    }) as Promise<SyncReport | SyncErrorResponse>;
}

/**
 * Records which repo a project pushes to, so a server-side sync knows the target without the
 * browser having to supply it. Best-effort from the caller's point of view: the repo selection
 * also stays in localStorage, which is where it lived before this existed.
 */
export function saveGithubTarget(
    projectId: string,
    target: { repoFullName?: string; projectV2Id?: string },
): Promise<{ success: true; integrations: ProjectIntegrations } | ErrorResponse> {
    return putJson(`/api/projects/${projectId}/integrations/github`, target, {
        networkErrorMessage: 'Could not save the GitHub target.',
    });
}
